<template>
  <div>
    <div id="overlay" v-if="fetchingData">
      <div class="spinner"></div>
    </div>
    <h3>{{application.projectType|capitalize}} Permit Application Review</h3>

    <expanded-fieldset title="Contact">
      <base-input v-model="application.contactEmail"
                  disabled
                  fieldName="Permit Contact Email"
                  placeholder="Primary Email Address"
                  type="email"/>

      <base-input v-model="application.confirmEmail"
                  fieldName="Permit Contact Confirm Email"
                  placeholder="Confirm Email Address"
                  disabled
                  type="email"/>
    </expanded-fieldset>
    <expanded-fieldset>
      <fieldset>
        <legend>Building Information</legend>
        <div class="form-group">
          <label class="required">Building Type</label>
          <div class="form-check">
            <input class="form-check-input"
                   type="radio"
                   name="facility-type"
                   v-model="application.facility.type"
                   value="Residential Single"
                   disabled
                   id="fac-type-res-single">
            <label class="form-check-label" for="fac-type-res-single">
              Residential (Single-Family Home)
            </label>
          </div>
          <div class="form-check">
            <input class="form-check-input"
                   type="radio"
                   name="facility-type"
                   v-model="application.facility.type"
                   value="Residential 24"
                   disabled
                   id="fac-type-res">
            <label class="form-check-label" for="fac-type-res">
              Residential (2-4 units)
            </label>
          </div>
          <div class="form-check">
            <input class="form-check-input"
                   type="radio"
                   name="facility-type"
                   v-model="application.facility.type"
                   value="Commercial"
                   disabled
                   id="fac-type-commercial">
            <label class="form-check-label" for="fac-type-commercial">
              Commercial (includes Industrial, Residential 5 or more units)
            </label>
          </div>
          <div class="form-check">
            <input class="form-check-input"
                   type="radio"
                   name="facility-type"
                   v-model="application.facility.type"
                   value="School"
                   disabled
                   id="fac-type-school">
            <label class="form-check-label" for="fac-type-school">
              School
            </label>
          </div>
          <div class="form-check">
            <input class="form-check-input"
                   type="radio"
                   name="facility-type"
                   v-model="application.facility.type"
                   value="Hospital"
                   disabled
                   id="fac-type-hospital">
            <label class="form-check-label" for="fac-type-hospital">
              Hospital
            </label>

            <div class="invalid-feedback">
              Building Type is required.
            </div>
            <span class="form-text">
                For commercial, school, and hospital facilities, one application must be completed per building.
              </span>
          </div>
        </div>
        <div v-if="application.projectType.toLowerCase() === 'demolition' &&
          application.facility.type &&
          application.facility.type.startsWith('Residential')">
          <strong>How many of each type of building are being demolished? (If 0, leave blank)</strong>
          <div class="row">
            <div class="col">
              <base-input v-model="application.facility.houseCount"
                          label="House"
                          placeholder="(i.e., 1)"
                          v-numeric
                          disabled
                          min="0"
                          mask="####"
                          type="number"/>
            </div>
            <div class="col">
              <base-input v-model="application.facility.garageCount"
                          label="Garage"
                          placeholder="(i.e., 1)"
                          v-numeric
                          disabled
                          min="0"
                          mask="####"
                          type="number"/>
            </div>
            <div class="col">
              <base-input v-model="application.facility.shedCount"
                          label="Shed"
                          placeholder="(i.e., 1)"
                          v-numeric
                          min="0"
                          mask="####"
                          disabled
                          type="number"/>
            </div>
          </div>
        </div>

        <base-input v-model="application.facility.name"
                    label="Building Name"
                    placeholder="Name"
                    disabled
                    maxlength="25"/>

        <v-address v-model="application.facility.address"
                   :restrictState="true"
                   :chicagoLandCities="true"
                   labelPrefix="Facility Address"
                   disabled
                   :show-unincorporated="true"/>

        <div class="row">
          <div class="col">
            <base-input v-model="application.facility.contactName"
                        label="Building Contact Name"
                        disabled
                        placeholder="First Name"/>
          </div>
          <div class="col">
            <base-input v-model="application.facility.contactPhone"
                        label="Building Contact Phone"
                        type="tel"
                        mask="(###)-###-####"
                        disabled
                        placeholder="(xxx)-xxx-xxxx"/>
          </div>
          <div class="col">
            <base-input v-model="application.facility.contactEmail"
                        label="Building Contact Email"
                        type="email"
                        disabled
                        placeholder="name@company.com"/>
          </div>
        </div>
        <base-input v-model="application.facility.priorUse"
                    label="Facility Prior Use"
                    disabled
                    placeholder="Prior Use"
                    maxlength="50"/>
        <base-input v-model="application.facility.presentUse"
                    label="Facility Present Use"
                    placeholder="Present Use"
                    disabled
                    maxlength="50"/>
        <base-input v-model="application.facility.futureUse"
                    label="Facility Future Use"
                    disabled
                    placeholder="Future Use"
                    maxlength="50"/>
        <div class="row">
          <div class="col-12 col-lg">
            <base-input v-model="application.facility.buildingSize"
                        label="Building Size (Sq Ft)"
                        placeholder="Sq Ft"
                        disabled
                        min="0"
                        type="number"/>
          </div>
          <div class="col-12 col-lg">
            <base-input v-model="application.facility.buildingFloors"
                        label="Building # of Floors"
                        disabled
                        placeholder="# of Floors"
                        min="0"
                        type="number"/>
          </div>
        </div>

        <div class="row align-items-end">
          <div class="col" v-if="application.projectType.toLowerCase() === 'demolition'">
            <label>
              If there is asbestos present, asbestos must be removed/abated through the CCDES permitting process
            </label>
          </div>
          <div class="w-100"></div>
          <div class="col" v-if="application.projectType.toLowerCase() === 'demolition'">
            <yes-no v-model="application.asbestosPresent" label="Is asbestos present?" disabled/>
          </div>
          <div class="w-100"></div>
        </div>

        <div class="row" v-if="application.facility.builtBefore1982">
          <div class="col">
            <label>
              Attach supporting document showing year of building construction, such as construction permit, deed,
              appraisal date, etc.
            </label>
            <base-file-uploader v-model="application.files.buildingConstructionDate"
                                label="Supporting Documentation"
                                disabled
                                :inputVisible="false"
                                :file="application.existingFileDetails.buildingConstructionDate" />
          </div>
        </div>
      </fieldset>

      <fieldset>
        <legend>Building Owner Information</legend>
        <div class="row">
          <div class="col">
            <base-input v-model="application.facility.contact"
                        label="Facility Owner Name"
                        disabled
                        placeholder="Owner Name"/>
          </div>
        </div>
        <v-address v-model="application.facility.ownerAddress"
                   labelPrefix="Owner Address"
                   disabled
                   :show-unincorporated="false"/>
        <div class="row">
          <div class="col">
            <base-input v-model="application.facility.phone"
                        label="Facility Owner Phone"
                        type="tel"
                        disabled
                        mask="(###)-###-####"
                        placeholder="(xxx)-xxx-xxxx"/>
          </div>
          <div class="col">
            <base-input v-model="application.facility.email"
                        label="Facility Owner Email"
                        type="email"
                        disabled
                        placeholder="name@company.com"/>
          </div>
        </div>
      </fieldset>
    </expanded-fieldset>
    <expanded-fieldset title="Project Information">
      <fieldset>
        <legend>Contractor Information</legend>
        <div class="row">
          <div class="col">
            <label class="required">{{application.projectType | capitalize}} Contractor Name</label>
            <base-input v-model="application.contractor.name" disabled/>
          </div>
          <div class="col" v-if="application.projectType.toLowerCase() === 'asbestos'">
            <base-input v-model="application.contractor.contractorIDPH"
                        disabled
                        label="IDPH Number"/>
          </div>
        </div>

        <hr />
        <v-address v-model="application.contractor.address"
                   :disabled="true"
                   labelPrefix="Contractor"/>
        <hr />
        <div class="row">
          <div class="col">
            <base-input v-model="application.contractorContactFirstName"
                        label="Contractor First Name"
                        disabled
                        placeholder="First Name"/>
          </div>
          <div class="col">
            <base-input v-model="application.contractorContactLastName"
                        label="Contractor Last Name"
                        disabled
                        placeholder="Last Name"/>
          </div>
          <div class="col">
            <base-input v-model="application.contractorContactPhone"
                        label="Contractor Phone"
                        disabled
                        type="tel"
                        placeholder="(xxx)-xxx-xxxx"
                        mask="(###)-###-####"/>
          </div>
          <div class="col">
            <base-input v-model="application.contractorContactEmail"
                        label="Contractor Email"
                        disabled
                        type="email"
                        placeholder="name@company.com"/>
          </div>
        </div>
      </fieldset>
      <fieldset disabled>
        <legend>Variance Information</legend>
        <div class="row">
          <div class="col-9">
            <label>
              Does the current situation pose an imminent health and safety risk to the public?
            </label>
          </div>
          <div class="col">
            <yes-no v-model="application.varianceCheckPublicHealth"/>
          </div>
        </div>

        <div class="row">
          <div class="col-9">
            <label>
              Has a sudden unplanned event occurred that would cause equipment failure or an unreasonable financial burden?
            </label>
          </div>
          <div class="col">
            <yes-no v-model="application.varianceCheckFinancialBurden"/>
          </div>
        </div>

        <div class="row">
          <div class="col-9">
            <label>
              Are there official documents from a town/village/municipality in the form of an
              order related to the condition of the structure? Are there official documents from a court in the form of an order?
            </label>
          </div>
          <div class="col">
            <yes-no v-model="application.varianceCheckCourtOrder" />
          </div>
        </div>

        <div class="row" v-if="isVariance">
          <div class="col">
            <base-checkbox v-model="application.varianceNoRefundAcknowledgement"
                           label="I understand that by answering yes to any of these questions, I will be charged a
                           variance fee and that fee is not refundable."
                           message="Field must be checked."/>
          </div>
          <div class="w-100"></div>
          <div class="col">
            <base-input v-model="application.varianceNotesPublicHealth"
                        maxlength="500"
                        label="Explain:"
                        placeholder="Explanation"/>
          </div>
          <div class="w-100"></div>
          <div class="col">
            <label>
              When requesting a variance, you must submit documentation supporting the emergency. Sample documentation includes:
            </label>
            <ul>
              <li>Letters from property owners</li>
              <li>Letters from contractors</li>
              <li>Letters from villages/municipalities/town officials</li>
              <li>Court orders from every level</li>
            </ul>
          </div>
          <div class="col">
            <base-file-uploader v-model="application.files.varianceHealthRisk"
                                label="Supporting Documentation"
                                :file="application.existingFileDetails.varianceHealthRisk"/>
          </div>
        </div>

      </fieldset>
      <fieldset>
        <legend>Project Information</legend>
        <div class="row">
          <div class="col-9">
            <label>
              Has the work already been started (or is complete)?
            </label>
          </div>
          <div class="col">
            <yes-no v-model="application.varianceCheckWorkStartedorComplete" disabled/>
          </div>
        </div>
        <div class="row" v-if="application.varianceCheckWorkStartedorComplete">
          <div class="col-2">
            <label class="required">
              Explain:
            </label>
          </div>
          <div class="col">
            <base-input v-model="application.varianceNotesWorkStartedorComplete"
                        maxlength="500"
                        disabled
                        placeholder="Explanation"/>
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-md-6">
            <base-date-picker v-model="application.startDate"
                              disabled
                              label="Start Date"/>
          </div>
          <div class="col-12 col-md-6">
            <base-date-picker v-model="application.endDate"
                              disabled
                              label="End Date"/>
          </div>
        </div>
        <label>Work Hours</label>
        <div class="row">
          <div class="col-12 col-md-6">
            <base-time-picker v-model="application.workHours.workHoursStart"
                              disabled
                              label="Start Time"/>
          </div>
          <div class="col-12 col-md-6">
            <base-time-picker v-model="application.workHours.workHoursEnd"
                              disabled
                              label="End Time"/>
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-md-6">
            <yes-no v-model="application.workHours.workWeekends" label="Working Weekends?" disabled />
          </div>
          <div class="col-12 col-md-6">
            <base-input v-model="this.partialFullDemoString"
                         label="Type of Demolition"
                         disabled>
            </base-input>
          </div>

        </div>

        <hr>

        <base-textarea v-model="application.specialCircumstances" label="Special Circumstances/Notes to Consider" disabled/>

        <hr>

        <base-input v-model="application.transporter.name"
                    disabled
                    label="Waste Transporter Name"/>

        <base-input v-model="application.disposalSite.name"
                    disabled
                    label="Disposal Site/Landfill Name"/>
      </fieldset>
    </expanded-fieldset>

    <expanded-fieldset title="Abatement Information"
                       v-if="application.projectType.toLowerCase() === 'asbestos'">
      <div class="row" v-if="application.facility.type === 'School'">
        <div class="col">
          <base-input v-model="application.asbestosData.projectManager"
                      disabled
                      label="Project Manager"/>
        </div>
        <div class="col">
          <base-input v-model="application.asbestosData.inspectorId"
                      disabled
                      label="Inspector ID"/>
        </div>
      </div>

      <div class="form-group">
        <label>What means will be used to remove asbestos? (please check all applicable)</label>
        <div class="form-check">
          <input class="form-check-input"
                 type="checkbox"
                 v-model="application.asbestosData.abatementHeat"
                 disabled
                 value="heat"
                 id="removal-type-heat">
          <label class="form-check-label" for="removal-type-heat">
            Heat
          </label>
        </div>
        <div class="form-check">
          <input class="form-check-input"
                 type="checkbox"
                 v-model="application.asbestosData.abatementGWR"
                 value="grosswet"
                 disabled
                 id="removal-type-grosswet">
          <label class="form-check-label" for="removal-type-grosswet">
            Gross Wet Removal
          </label>
        </div>
        <div class="form-check">
          <input class="form-check-input"
                 type="checkbox"
                 v-model="application.asbestosData.abatementInfrared"
                 value="infrared"
                 disabled
                 id="removal-type-infrared">
          <label class="form-check-label" for="removal-type-infrared">
            Infrared
          </label>
        </div>
        <div class="form-check">
          <input class="form-check-input"
                 type="checkbox"
                 disabled
                 v-model="application.asbestosData.abatementGlovebag"
                 value="glovebag" id="removal-type-glovebag">
          <label class="form-check-label" for="removal-type-glovebag">
            Glovebag
          </label>
        </div>
        <div class="form-check">
          <input class="form-check-input"
                 type="checkbox"
                 v-model="application.asbestosData.abatementSolvent"
                 value="solvent"
                 disabled
                 id="removal-type-solvent">
          <label class="form-check-label" for="removal-type-solvent">
            Solvent
          </label>
        </div>
        <div class="form-check">
          <input class="form-check-input"
                 type="checkbox"
                 v-model="application.asbestosData.abatementOther"
                 value="other"
                 disabled
                 id="removal-type-other">
          <label class="form-check-label" for="removal-type-other">
            Other (Please describe)
          </label>
        </div>
        <base-input v-model="application.asbestosData.asbestosRemovalMethodsOther"
                    placeholder="Other abatement method"
                    disabled>
        </base-input>
      </div>
      <label>Quantities</label>
      <p>
        Tip: CAT I non-friable ACM are asbestos-containing resilient floor coverings (vinyl asbestos tile (VAT),
        asphalt roofing products, packing and gaskets. All other non-friable ACM are considered CAT II non-friable ACM.
        (RACM) is:
      </p>
      <ol type="a">
        <li>Friable asbestos material</li>
        <li>Category I non-friable ACM that has become friable</li>
        <li>Category I non-friable ACM that will be or has been subjected to sanding, grinding, cutting or abrading</li>
        <li>
          Category II non-friable ACM that has a high probability of becoming or has become crumbled, pulverized or reduced
          to powder by the forces expected to act on the material in the course of demolition or renovation operations.
        </li>
      </ol>
      <br />
      <!-- START: Grid layout -->
      <div class="form-group">
        <table class="table table-striped table-bordered">
          <colgroup span="2"></colgroup>
          <colgroup span="2"></colgroup>
          <thead>
          <tr>
            <th rowspan="2"> </th>
            <th rowspan="2">RACM to be Removed</th>
            <th colspan="2">Non-friable Asbestos to be Removed</th>
            <th rowspan="2">Total Removed</th>
          </tr>
          <tr>
            <th scope="col">CAT I</th>
            <th scope="col">CAT II</th>
          </tr>
          </thead>
          <tr>
            <th scope="row">Pipes (Linear Ft)</th>
            <td>
              <base-input v-model="application.asbestosData.pipesRACM" disabled/>
            </td>
            <td>
              <base-input v-model.number="application.asbestosData.pipesNonFriableRemovalCat1"
                          mask="#####"
                          min="0"
                          disabled
                          type="number"/>
            </td>
            <td>
              <base-input v-model.number="application.asbestosData.pipesNonFriableRemovalCat2"
                          mask="#####"
                          min="0"
                          disabled
                          type="number"/>
            </td>
            <td>
              <base-input v-model.number="application.asbestosData.linearftTotalRemoval"
                          mask="######"
                          min="0"
                          disabled
                          type="number"/>
            </td>
          </tr>
          <tr>
            <th scope="row">Surface Area (Sq Ft)</th>
            <td>
              <base-input v-model="application.asbestosData.surfaceAreaRACM" disabled/>
            </td>
            <td>
              <base-input v-model.number="application.asbestosData.surfaceAreaNonFriableRemovalCat1"
                          mask="#####"
                          min="0"
                          disabled
                          type="number"/>
            </td>
            <td>
              <base-input v-model.number="application.asbestosData.surfaceAreaNonFriableRemovalCat2"
                          mask="#####"
                          min="0"
                          disabled
                          type="number"/>
            </td>
            <td>
              <base-input v-model.number="application.asbestosData.surfaceAreaTotalRemoval"
                          mask="######"
                          min="0"
                          disabled
                          type="number"/>
            </td>
          </tr>
        </table>
      </div>
    </expanded-fieldset>

    <expanded-fieldset title="Signature">
      <div class="row" v-if="application.projectType.toLowerCase() === 'demolition'">
        <div class="col-12 col-md-6">
          <base-file-uploader v-model="application.files.platOfSurvey"
                              disabled
                              :inputVisible="false"
                              :file="application.existingFileDetails.platOfSurvey"
                              label="Plat of Survey/Photographs of Structure" />
        </div>
        <div class="col-12 col-md-6">
          <base-file-uploader v-model="application.files.taxSummary"
                              disabled
                              :inputVisible="false"
                              :file="application.existingFileDetails.taxSummary"
                              label="Copy of Official Cook County Tax Bill" />
        </div>
        <div class="col-12 col-md-6">
          <base-file-uploader v-model="application.files.supportingDocument"
                              disabled
                              :inputVisible="false"
                              :file="application.existingFileDetails.supportingDocument"
                              label="Supporting Document" />
        </div>
      </div>
      <p>
        <small>
          I certify that the statements in this application are true and accurate to the best of my knowledge and belief;
          and, that all work under the proposed permit will conform to the drawings, specifications, and other information
          attached to this application; and, will conform to the Cook County Environmental Code Ordinance. All fees are non-refundable and credits will not be given.
        </small>
      </p>
      <base-input v-model="application.name"
                  label="Owner, Applicant, or Authorized Agent:"
                  disabled
                  placeholder="Full Name"/>
      <base-input v-model="application.title"
                  disabled
                  label="Submitter's Title"/>
      <base-date-picker v-model="application.signDate"
                        disabled
                        label="Date"/>
    </expanded-fieldset>

    <div>
      <button type="button"
              class="btn btn-secondary"
              @click="cancel">
        Back
      </button>
      <button type="button"
              class="btn btn-primary"
              @click="checkout">
        Checkout
      </button>
    </div>
  </div>
</template>

<script>
import { PermitApplication } from '@/js/FormObjects';
import { CapitalizeFilter } from '@/js/Mixins';
import cloneDeep from 'lodash/fp/cloneDeep';
import ExpandedFieldset from '@/components/ExplodedFieldset';
import BaseInput from '@/components/BaseInput';
import VAddress from '@/components/VAddress';
import BaseSelect from '@/components/BaseSelect';
import YesNo from '@/components/YesNo';
import BaseTimePicker from '@/components/BaseTimePicker';
import BaseDatePicker from '@/components/BaseDatePicker';
import BaseTextarea from '@/components/BaseTexarea';
import BaseCheckbox from '@/components/BaseCheckbox';
import BaseFileUploader from '@/components/BaseFileUploader';

export default {
  name: 'PermitApplicationReview',
  components: {
    ExpandedFieldset, BaseInput, VAddress, BaseSelect, YesNo, BaseTimePicker, BaseDatePicker, BaseTextarea, BaseCheckbox, BaseFileUploader,
  },
  mixins: [CapitalizeFilter],
  props: ['projectType'],
  data() {
    return {
      application: new PermitApplication(this.projectType),
      fetchingData: false,
    };
  },
  mounted() {
    this.application = cloneDeep(this.$store.state.application);
    // this.$store.commit('permitApplication', null)
    if (!(this.application)) {
      this.$notify('No permit application found.', 'error');
      this.$router.push({ name: 'PermitLanding', params: { projectType: this.projectType } });
    }
  },
  methods: {
    checkout() {
      const self = this;

      const formData = new FormData();
      Object.keys(this.application.files).forEach((key) => {
        if (self.application.files[key]) {
          formData.append(key, self.application.files[key]);
        }
      });
      formData.append('form', JSON.stringify(this.application))
      this.fetchingData = true
      self.$http.post('/api/permit/review', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
        .then((response) => {
          self.fetchingData = false;
          this.$router.push({ name: 'Checkout', query: { permitId: response.data } });
        })
        .catch((error) => {
          console.error(error);
          self.fetchingData = false;
          self.$notify('Error saving application record', 'error');
        });
    },
    cancel() {
      this.$store.commit('temporaryForm', this.application);
      this.$router.push({
        name: 'PermitApplication',
        params: {
          projectType: this.projectType,
        },
        query: {
          applicationNumber: this.application.id,
        },
      });
    },
  },
  computed: {
    isVariance() {
      return this.application.varianceCheckPublicHealth ||
        this.application.varianceCheckFinancialBurden ||
        this.application.varianceCheckCourtOrder;
    },
    partialFullDemoString() {
      return (this.application.partialFullDemo == 'F') ? 'Full Demolition' : 'Partial Demolition';
    },
  },
};
</script>
